import React, { useEffect, useState } from "react";
import styled, { withTheme } from "styled-components";
import PropTypes from "prop-types";
import { roundMealValue } from "../../../../helpers/mealsHelper";

const Title = styled.span`
  color: ${(props) => props.theme?.mortar};
  font-size: 15px;
  font-weight: 600;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-right: 80px;
  margin-top: 16px;
`;

const Magnitude = styled.span`
  width: 20px;
  font-size: 18px;
  color: ${(props) => props.theme?.blackLite};
`;

const Percentage = styled.input`
  max-width: 55px;
  text-align: center;
  font-weight: 600 !important;
  height: auto !important;
  padding: 0 !important;
  font-size: 15px !important;

  &:hover,
  &:focus {
    border-bottom: 1px solid ${(props) => props.theme?.darkPurple};
  }
`;

const Grams = styled.input`
  max-width: 70px;
  text-align: center;
  height: auto !important;
  padding: 0 !important;
  font-weight: 600 !important;
  font-size: 15px !important;

  &:disabled,
  &[readonly] {
    background-color: transparent;
  }
`;

const Info = styled.span`
  font-size: 12px;
  font-weight: 400;
  margin-top: 5px;
  color: ${(props) => props.theme?.blackLite};
`;

const Separator = styled.span`
  margin-left: 40px;
  margin-right: 40px;
`;

const PercentageInput = (props) => {
  const {
    title,
    magnitude,
    defaultValue,
    onChange,
    calories,
    kCalGramsRelation,
    className,
    info,
  } = props;

  const [percentage, setPercentage] = useState("");
  const [grams, setGrams] = useState("");

  useEffect(() => {
    if (defaultValue && percentage === "0%") {
      setPercentage(`${defaultValue}%`);
    }
  }, [defaultValue, percentage]);

  const inputChanged = (e) => {
    const tempValue = e.target.value;

    if (
      tempValue === "" ||
      /^(100(\.00?)?|[1-9]?\d(\.\d\d?)?)$/.test(tempValue)
    ) {
      setPercentage(tempValue);
    }
  };

  const onInputFocus = () => {
    setPercentage((prevState) => prevState.replace("%", ""));
  };

  const onInputBlur = () => {
    setPercentage((prevState) => `${prevState === "" ? 0 : prevState}%`);
  };

  useEffect(() => {
    onInputBlur();
  }, []);

  useEffect(() => {
    const percentageNumber = Number(percentage.replace("%", ""));
    const gramsNumber = roundMealValue(grams.replace("g", ""));

    onChange(percentageNumber, gramsNumber);
  }, [percentage, onChange, grams]);

  useEffect(() => {
    if (percentage && calories && kCalGramsRelation) {
      const percentageNumber = Number(percentage.replace("%", ""));

      const tempGrams = `${roundMealValue(
        (Number(calories) * (percentageNumber / 100)) / kCalGramsRelation
      )}g`;
      setGrams(tempGrams);

      const gramsNumber = roundMealValue(tempGrams.replace("g", ""));

      onChange(percentageNumber, gramsNumber);
    }
  }, [calories, kCalGramsRelation, onChange, percentage]);

  return (
    <div className={`d-flex flex-column ${className}`}>
      <Title>{title}</Title>
      <Row>
        <Magnitude>{magnitude}</Magnitude>
        <Percentage
          data-testid="percentage-input"
          type="text"
          className="form-control"
          value={percentage}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          onChange={inputChanged}
        />
        <Separator className="text">/</Separator>
        <Grams
          data-testid="grams-input"
          type="text"
          className="form-control"
          value={grams}
          disabled
          readOnly
        />
      </Row>
      {info && <Info>{info}</Info>}
    </div>
  );
};

PercentageInput.defaultProps = {
  className: "",
  info: null,
  defaultValue: null,
};

PercentageInput.propTypes = {
  title: PropTypes.string.isRequired,
  magnitude: PropTypes.string.isRequired,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  kCalGramsRelation: PropTypes.number.isRequired,
  className: PropTypes.string,
  info: PropTypes.string,
};

export default withTheme(PercentageInput);
